/**
 * Frontend fallback: derives discovery metadata from curated views.
 * Content is primarily derived from semantic model fields, with lightweight
 * heuristics (domain keywords, business area labels) for categorization.
 */

import type { CuratedView } from '../business-views/viewCuration';
import type {
  DiscoveryData,
  KeyQuestion,
  QuickInsight,
  RecommendedDashboard,
  BusinessDatasetItem,
} from '../../types/discovery';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function truncateDescription(desc: string, maxWords = 20): string {
  if (!desc) return '';
  // Remove trailing period if present for cleaner look
  let clean = desc.trim().replace(/\.$/, '');
  const words = clean.split(/\s+/);
  if (words.length <= maxWords) return clean;
  return words.slice(0, maxWords).join(' ') + '…';
}

/** Convert a full question into a short analytical topic */
function toDiscoveryTopic(question: string): string {
  // Strip leading question words and capitalize
  let topic = question
    .replace(/^(which|what|how|where|when|who|why|are there|is there|do we|can we)\s+/i, '')
    .replace(/\?$/, '')
    .trim();
  // Capitalize first letter
  topic = topic.charAt(0).toUpperCase() + topic.slice(1);
  // Truncate if too long
  if (topic.length > 40) {
    topic = topic.slice(0, 39).replace(/\s+\S*$/, '') + '…';
  }
  return topic;
}

const BUSINESS_AREA_LABELS: Record<string, string> = {
  sales: 'Sales Pipeline',
  finance: 'Finance',
  marketing: 'Marketing',
  support: 'Customer Support',
  operations: 'Operations',
  general: 'Business Analytics',
};

function isCrossDomainView(cv: CuratedView): boolean {
  const vt = cv.view.viewType?.toLowerCase() || '';
  return vt.includes('multi_fact') || vt.includes('multi-fact');
}

/** Map raw table/view names to business domain labels */
const DOMAIN_KEYWORDS: [RegExp, string][] = [
  [/opportunity|pipeline|deal/i, 'Sales Pipeline'],
  [/lead/i, 'Leads'],
  [/contact/i, 'Customer Contacts'],
  [/account|customer/i, 'Customers'],
  [/order/i, 'Orders'],
  [/product/i, 'Products'],
  [/event|activity|task|call|meeting/i, 'Activities'],
  [/campaign/i, 'Campaigns'],
  [/invoice|payment/i, 'Payments'],
  [/ticket|case/i, 'Support'],
];

function tableToBusinessDomain(tableName: string): string {
  const text = tableName.toLowerCase();
  for (const [regex, label] of DOMAIN_KEYWORDS) {
    if (regex.test(text)) return label;
  }
  // Fallback: clean up the name
  const cleaned = tableName.replace(/_fact$|_dim$|_view$/i, '').replace(/_/g, ' ');
  return cleaned.replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildBusinessContextLabel(cv: CuratedView, displayLabels: ReadonlyMap<string, string>): string {
  if (isCrossDomainView(cv)) {
    const tables = cv.view.baseTables;
    if (tables && tables.length >= 2) {
      const domain1 = displayLabels.get(tables[0]) || tableToBusinessDomain(tables[0]);
      const domain2 = displayLabels.get(tables[1]) || tableToBusinessDomain(tables[1]);
      if (domain1 !== domain2) return `${domain1} + ${domain2}`;
      return domain1;
    }
    return BUSINESS_AREA_LABELS[cv.businessArea] || 'Business Analytics';
  }
  return BUSINESS_AREA_LABELS[cv.businessArea] || 'Business Analytics';
}

/** Score a view for recommendation quality */
function scoreView(cv: CuratedView): number {
  let score = 0;
  if (cv.shortDescription) score += 20;
  if (cv.exampleQuestions.length > 0) score += 20;
  if (cv.exampleQuestions.length >= 3) score += 10;
  if (cv.keyMetrics.length > 0) score += 15;
  if (cv.keyMetrics.length >= 3) score += 5;
  if (cv.columnCount > 5) score += 10;
  if (cv.columnCount > 15) score += 5;
  if (cv.recommendedForDashboard) score += 15;
  // Slight boost for sortOrder (lower = better)
  score += Math.max(0, 10 - cv.view.sortOrder);
  return score;
}

// ─── Main derivation ──────────────────────────────────────────────────────────

export function deriveDiscoveryFromViews(
  curated: CuratedView[], displayLabels: ReadonlyMap<string, string> = new Map(),
): DiscoveryData {
  const primaryViews = curated.filter((v) => v.importance === 'primary');
  const allViews = curated;

  // Score and sort all primary views
  const scored = primaryViews
    .map((cv) => ({ cv, score: scoreView(cv) }))
    .filter(({ cv }) => cv.shortDescription || cv.exampleQuestions.length > 0)
    .sort((a, b) => b.score - a.score);

  // Select top 6 with cross-domain inclusion rule
  const crossDomain = scored.filter(({ cv }) => isCrossDomainView(cv));

  let selected = scored.slice(0, 6);

  // If cross-domain exists but isn't in top 6, apply 15% rule
  const selectedCrossCount = selected.filter(({ cv }) => isCrossDomainView(cv)).length;
  if (selectedCrossCount < 2 && crossDomain.length > 0) {
    const lowestSelectedScore = selected.length > 0 ? selected[selected.length - 1].score : 0;
    const threshold = lowestSelectedScore * 0.85;
    const eligibleCross = crossDomain.filter(
      ({ score, cv }) => score >= threshold && !selected.some((s) => s.cv.view.id === cv.view.id)
    );
    if (eligibleCross.length > 0) {
      const needed = Math.min(2 - selectedCrossCount, eligibleCross.length);
      // Replace lowest non-cross-domain entries
      const nonCrossInSelected = selected.filter(({ cv }) => !isCrossDomainView(cv));
      const crossInSelected = selected.filter(({ cv }) => isCrossDomainView(cv));
      const kept = nonCrossInSelected.slice(0, 6 - needed - crossInSelected.length);
      selected = [...crossInSelected, ...kept, ...eligibleCross.slice(0, needed)]
        .sort((a, b) => b.score - a.score)
        .slice(0, 6);
    }
  }

  // Build KeyQuestion cards
  const keyQuestions: KeyQuestion[] = selected.map(({ cv, score }, index) => {
    const topics = cv.exampleQuestions.slice(0, 3).map(toDiscoveryTopic);
    const kpiCount = Math.max(cv.keyMetrics.length, 3);
    const chartCount = Math.min(Math.max(Math.round(cv.columnCount * 0.6), 4), 12);
    return {
      id: `kq-${cv.view.id}`,
      title: cv.displayName,
      businessContextLabel: buildBusinessContextLabel(cv, displayLabels),
      description: truncateDescription(cv.shortDescription),
      questions: topics,
      relatedMetrics: cv.keyMetrics.slice(0, 3),
      primaryViewId: cv.view.id,
      supportingViewIds: [],
      dashboardPrompt: `Create a dashboard for: ${cv.displayName}. Focus on: ${topics.join('; ')}`,
      rank: index + 1,
      recommendationScore: score,
      isCrossDomain: isCrossDomainView(cv),
      kpiCount,
      chartCount,
    };
  });

  // Recommended dashboards (legacy field, keep populated)
  const recommendedDashboards: RecommendedDashboard[] = keyQuestions.map((kq) => ({
    id: `rd-${kq.primaryViewId}`,
    title: `${kq.title} Dashboard`,
    description: kq.description,
    kpis: kq.relatedMetrics,
    sections: kq.questions,
    primaryViewId: kq.primaryViewId,
    dashboardPrompt: kq.dashboardPrompt,
    rank: kq.rank,
  }));

  // Quick insights from metadata
  const quickInsights: QuickInsight[] = [];
  for (const cv of primaryViews.slice(0, 5)) {
    if (quickInsights.length >= 5) break;
    if (!cv.shortDescription) continue;

    quickInsights.push({
      id: `qi-${cv.view.id}`,
      title: cv.displayName,
      description: cv.shortDescription,
      type: 'suggested',
      relatedViewId: cv.view.id,
    });
  }

  // Business datasets — derive entity chips from baseTables
  function deriveEntityChips(cv: CuratedView): string[] {
    const tables = cv.view.baseTables || [];
    const chips = tables.map(name => displayLabels.get(name) || tableToBusinessDomain(name));
    // Deduplicate and limit to 3
    return [...new Set(chips)].slice(0, 3);
  }

  /** Rename entity-only views to dashboard-oriented names */
  const ENTITY_DASHBOARD_NAMES: Record<string, string> = {
    'Account': 'Account Performance',
    'Contact': 'Contact Coverage',
    'User': 'User Activity',
    'Product': 'Product Analysis',
    'Product2': 'Product Analysis',
    'Pricebook': 'Pricing Analysis',
    'Pricebook2': 'Pricing Analysis',
    'Pricebook Entry': 'Pricebook Performance',
  };

  function toDashboardName(name: string): string {
    // Try exact match after stripping " View"
    const base = name.replace(/\s+View$/i, '').trim();
    if (ENTITY_DASHBOARD_NAMES[base]) return ENTITY_DASHBOARD_NAMES[base];
    // Generic fallback: "X View" → "X Analysis"
    if (/\s+View$/i.test(name)) return `${base} Analysis`;
    return name;
  }

  /** Determine if a view is a simple entity/table view (single base table, dimension type) */
  function isEntityView(cv: CuratedView): boolean {
    const vt = cv.view.viewType?.toLowerCase() || '';
    return vt.includes('dimension') || (cv.view.baseTables?.length === 1 && !vt.includes('fact'));
  }

  /** Generate synthetic description when none exists */
  function syntheticDescription(_name: string, chips: string[]): string {
    if (chips.length >= 2) {
      return `Analyze ${chips.slice(0, 2).join(' and ').toLowerCase()} trends, patterns, and key metrics`;
    }
    if (chips.length === 1) {
      return `Explore ${chips[0].toLowerCase()} data, trends, and performance insights`;
    }
    return `Explore key metrics, trends, and performance patterns`;
  }

  /** Generate synthetic questions when none exist */
  function syntheticQuestions(name: string, chips: string[]): string[] {
    const entity = chips[0] || name.replace(/\s+(Performance|Coverage|Activity|Analysis)$/i, '');
    return [
      `Top performing ${entity.toLowerCase()} by key metrics`,
      `${entity} trends and patterns over time`,
    ];
  }

  function toBizDatasetItem(cv: CuratedView): BusinessDatasetItem {
    const name = isEntityView(cv) ? toDashboardName(cv.displayName) : cv.displayName;
    const chips = deriveEntityChips(cv);
    const crossDomain = isCrossDomainView(cv);

    // Use real questions if available, otherwise synthetic
    let questions = cv.exampleQuestions.slice(0, 2).map(toDiscoveryTopic);
    if (questions.length === 0) {
      questions = syntheticQuestions(name, chips);
    }

    // Use real description if available, otherwise synthetic
    let description = cv.shortDescription ? truncateDescription(cv.shortDescription, 15) : '';
    if (!description) {
      description = syntheticDescription(name, chips);
    }

    return {
      id: cv.view.id,
      name,
      description,
      fieldsCount: cv.columnCount,
      entityChips: chips,
      questions,
      dashboardPrompt: `Create a dashboard for: ${name}. Focus on: ${questions.join('; ')}`,
      isCrossDomain: crossDomain,
      viewId: cv.view.id,
    };
  }

  // Collect all non-primary views for the "More Dashboard Ideas" section
  const nonPrimaryViews = allViews.filter((v) => v.importance !== 'primary');

  // Sort: complex dashboards first, entity views last
  const sortedNonPrimary = [...nonPrimaryViews].sort((a, b) => {
    const aEntity = isEntityView(a) ? 1 : 0;
    const bEntity = isEntityView(b) ? 1 : 0;
    if (aEntity !== bEntity) return aEntity - bEntity;
    // Among same category, sort by column count (richer first)
    return b.columnCount - a.columnCount;
  });

  const recommended: BusinessDatasetItem[] = sortedNonPrimary.slice(0, 5).map(toBizDatasetItem);
  const secondary: BusinessDatasetItem[] = sortedNonPrimary.slice(5, 11).map(toBizDatasetItem);
  const advanced: BusinessDatasetItem[] = sortedNonPrimary.slice(11).map(toBizDatasetItem);

  return {
    keyQuestions,
    quickInsights,
    recommendedDashboards,
    businessDatasets: { recommended, secondary, advanced },
  };
}